/**
 * Header "Naše služby" dropdown — registry-driven services menu.
 *
 * Lists every available process module from ``GET /api/v1/processes``
 * that also has its own FE configurator registered. Each link routes
 * to ``/services/{slug}``; "Brzy" modules render with a disabled badge
 * so customers see what's coming without being able to click through.
 *
 * Surface finishes are intentionally excluded — they're a per-part
 * modifier inside other configurators, not a standalone service. The
 * catalog of finish types lives at the bottom of ``/materials``.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useProcesses } from "@/api/queries/processes";
import { hasConfigurator, sortByLandingOrder } from "@/processes/registry";
import { cx } from "@/lib/utils";

export function ServicesDropdown({
  lang,
  onNavigate,
}: {
  lang: string;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation("common");
  const processes = useProcesses();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // ``useProcesses()`` already IS the landing set — the default list is the
  // tenant's graph entry points (O4c), so there is no per-row candidacy flag
  // left to re-check here.
  const items = sortByLandingOrder(
    (processes.data ?? []).filter((p) => hasConfigurator(p)),
  );

  const close = () => {
    setOpen(false);
    onNavigate?.();
  };

  // Jednopoložkový režim: degradujeme dropdown na přímý link
  // v hlavičce — vizuálně sjednocený s ostatními PrimaryNav linky.
  if (items.length === 1) {
    const p = items[0];
    const disabled = !p.is_available;
    const cls = cx(
      "rounded-md px-2 py-1 text-sm transition-colors",
      disabled ? "text-ink-3" : "text-ink-2 hover:text-ink",
    );
    const label = (
      <>
        <span>{p.display_name}</span>
        {disabled && (
          <span className="ml-2 rounded-full bg-ink-4 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white">
            {t("nav.servicesSoon", { defaultValue: "Brzy" })}
          </span>
        )}
      </>
    );
    if (disabled) {
      return (
        <span
          className={cx(cls, "cursor-not-allowed")}
          aria-disabled
          data-testid={`nav-services-item-${p.slug}`}
        >
          {label}
        </span>
      );
    }
    return (
      <Link
        to={`/${lang}/services/${p.slug}`}
        onClick={() => onNavigate?.()}
        className={cls}
        data-testid={`nav-services-item-${p.slug}`}
      >
        {label}
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cx(
          "rounded-md px-2 py-1 text-sm transition-colors",
          open ? "font-medium text-ink" : "text-ink-2 hover:text-ink",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-services-toggle"
      >
        {t("nav.services", { defaultValue: "Naše služby" })}
        <span aria-hidden className="ml-1 text-[10px]">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-1 min-w-[14rem] rounded-md border border-border bg-surface p-1 shadow-md"
          data-testid="nav-services-menu"
        >
          {items.map((p) => {
            const disabled = !p.is_available;
            const cls = cx(
              "flex items-center justify-between rounded px-3 py-2 text-sm transition",
              disabled
                ? "cursor-not-allowed text-ink-3"
                : "text-ink hover:bg-surface-2",
            );
            const label = (
              <>
                <span>{p.display_name}</span>
                {disabled && (
                  <span className="ml-2 rounded-full bg-ink-4 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white">
                    {t("nav.servicesSoon", { defaultValue: "Brzy" })}
                  </span>
                )}
              </>
            );
            if (disabled) {
              return (
                <span
                  key={p.slug}
                  className={cls}
                  aria-disabled
                  data-testid={`nav-services-item-${p.slug}`}
                >
                  {label}
                </span>
              );
            }
            return (
              <Link
                key={p.slug}
                role="menuitem"
                to={`/${lang}/services/${p.slug}`}
                onClick={close}
                className={cls}
                data-testid={`nav-services-item-${p.slug}`}
              >
                {label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
